import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { PageSidesheetActions } from '@next/core-lib/components';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { TableColumnTypes, TableConfig } from '@next/core-lib/table';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, ReplaySubject } from 'rxjs';
import { map, startWith, take, takeUntil } from 'rxjs/operators';

import { PhosphateLegalNormFilter, PageTitles, SiteState, PhosphateLegalNorm, Crop } from '../../../shared/models';
import { DisplayService } from '../../../shared/services/display.service';
import {
  AddPhosphateLegalNorm,
  ClearPhosphateLegalNormFilter,
  DeletePhosphateLegalNorm,
  EditPhosphateLegalNorm,
  LoadPhosphateLegalNormsRequest,
  OpenPhosphateLegalNormImportForm,
  UpdateTotalNitrogenLegalNormFilter,
} from '../../../store/actions';
import { getCrops, getPhosphateLegalNormFilter, getPhosphateLegalNorms, getPhosphateLegalNormTotalNumberOfItems } from '../../../store/selectors';

@Component({
  selector: 'app-phosphate-legal-norm-list',
  templateUrl: './phosphate-legal-norm-list.component.html',
  standalone: false,
})
export class PhosphateLegalNormListComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly breakpoint = inject<BreakpointService>(BreakpointServiceInjector);
  readonly displayService = inject(DisplayService);
  private readonly dialog = inject(DialogService);
  private readonly locale = inject(LocaleService);

  @ViewChild('paginator', { static: false }) paginator: MatPaginator;
  title = PageTitles.PhosphateLegalNorm;
  tableConfig: TableConfig = {
    dataSource: new MatTableDataSource(),
    selectColumn: false,
    pagination: false,
    header: true,
    footer: false,
    disableCards: true,
    columns: [
      {
        id: 'calendarYear',
        label: this.locale.translate('year'),
        type: TableColumnTypes.NUMBER,
        disableFormatting: true,
      },
      {
        id: 'agrifirmCropId',
        label: this.locale.translate('columnHeader.agrifirmCropId'),
        type: TableColumnTypes.NUMBER,
        disableFormatting: true,
      },
      {
        id: 'cropName',
        label: this.locale.translate('cropName'),
        type: TableColumnTypes.STRING,
      },
      {
        id: 'conditionString',
        label: this.locale.translate('condition'),
        type: TableColumnTypes.STRING,
      },
      {
        id: 'isTransitionVariantString',
        label: this.locale.translate('isTransitionVariant'),
        type: TableColumnTypes.STRING,
      },
      {
        id: 'phosphateNorm',
        label: this.locale.translate('phosphateNorm'),
        type: TableColumnTypes.STRING,
      },
      {
        id: 'poorInPhosphateNorm',
        label: this.locale.translate('poorInPhosphateNorm'),
        type: TableColumnTypes.STRING,
      },
    ],
    actions: [
      {
        id: 'edit',
        label: this.locale.translate('edit'),
        icon: 'edit',
        callback: (event: PhosphateLegalNorm) => this.editNorm(event),
        disabled: () => false,
      },
      {
        id: 'delete',
        label: this.locale.translate('delete'),
        icon: 'delete',
        callback: (event: PhosphateLegalNorm) => this.deleteNorm(event),
        disabled: () => false,
      },
    ],
  };
  filterControl = new FormControl();
  defaultPageSize = 10;
  pageSizeOptions = [10, 25, 50];

  request: { pageNumber: number; pageSize: number; filter: PhosphateLegalNormFilter } = {
    pageNumber: 1,
    pageSize: 10,
    filter: {},
  };

  totalNumberOfItems$ = this.store.pipe(select(getPhosphateLegalNormTotalNumberOfItems));

  norms$ = this.store.pipe(select(getPhosphateLegalNorms));

  crops$ = this.store.pipe(select(getCrops));
  crops: Crop[] = [];

  filterValue$ = this.filterControl.valueChanges.pipe(
    map((value: string) => value || ''),
    startWith(''),
  );

  filterEnabled$ = new BehaviorSubject(false);
  isMobile$ = this.breakpoint.ltLg$;
  isEmpty$ = combineLatest([this.filterEnabled$, this.totalNumberOfItems$]).pipe(
    map(([filterEnabled, totalNumberOfItems]) => !filterEnabled && totalNumberOfItems === 0),
  );

  phosphateLegalNormFilter$ = this.store.pipe(select(getPhosphateLegalNormFilter));

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  ngOnInit() {
    this.crops$.subscribe((crops) => (this.crops = crops));

    this.norms$.pipe(takeUntil(this.destroyed$)).subscribe((norms) => {
      this.tableConfig.dataSource.data = norms.map((norm) => ({
        ...norm,
        cropName: this.getCropName(norm.cropId),
        conditionString: this.getConditionString(norm.condition),
        isTransitionVariantString: norm.isTransitionVariant ? this.locale.translate('yes') : '',
      }));
    });

    this.updateData();
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  updateData() {
    this.store.dispatch(new LoadPhosphateLegalNormsRequest(this.request.pageNumber, this.request.pageSize, this.request.filter));
  }

  updateFilter(data: PhosphateLegalNormFilter) {
    this.request.filter = data;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.request.pageNumber = 1;
    this.store.dispatch(new UpdateTotalNitrogenLegalNormFilter(data));
    this.store.dispatch(new LoadPhosphateLegalNormsRequest(this.request.pageNumber, this.request.pageSize, this.request.filter));
  }

  setFilterEnabled(isEnabled: boolean) {
    this.filterEnabled$.next(isEnabled);
  }

  resetFilter() {
    this.store.dispatch(new ClearPhosphateLegalNormFilter());
  }

  onPage(event: PageEvent) {
    this.request = {
      ...this.request,
      pageNumber: event.pageIndex + 1,
      pageSize: event.pageSize,
    };
    this.updateData();
  }

  addNorm() {
    this.store.dispatch(new AddPhosphateLegalNorm());
    if (this.paginator) {
      (this.paginator.length as number)++;
    }
  }

  editNorm(norm: PhosphateLegalNorm) {
    this.store.dispatch(new EditPhosphateLegalNorm(norm));
  }

  deleteNorm(norm: PhosphateLegalNorm) {
    this.dialog
      .confirm({
        title: this.locale.translate('delete'),
        text: this.locale.translate('deletePhosphateLegalNorm'),
        pushHistoryState: false,
      })
      .pipe(take(1))
      .subscribe((x) => {
        if (x.status === DIALOG_RETURN_TYPES.confirm) {
          this.store.dispatch(new DeletePhosphateLegalNorm(norm));
          if (this.paginator) {
            this.paginator.pageIndex = 0;
            (this.paginator.length as number)--;
          }
          this.request.pageNumber = 1;
          this.updateData();
        }
      });
  }

  importPhosphateLegalNorms() {
    this.store.dispatch(new OpenPhosphateLegalNormImportForm());
  }

  setSearch() {
    this.store.dispatch(PageSidesheetActions.openSidesheet());
  }

  getCropName = (cropId: number): string => this.crops?.find((c) => c.id === cropId)?.name ?? this.locale.translate('allOthers');

  getConditionString = (condition: string): string => this.displayService.getPhosphateConditionString(condition);
}
